import { TASK_PRIORITY } from '../../constants';
import { daysUntil } from '../../helpers';

export const VISITS_DUE_EVIDENCE_TYPE = 'cadence-drift';

export const DISMISS_REASONS = [
  { key: 'member-declined', label: 'Member declined for now', defaultReevalDays: 30 },
  {
    key: 'member-unreachable',
    label: 'Could not reach member or caregiver',
    defaultReevalDays: 14,
  },
  {
    key: 'not-medically-necessary',
    label: 'Not medically necessary yet (provider decision)',
    defaultReevalDays: 90,
    noteRequired: true,
  },
  {
    key: 'completed-elsewhere',
    label: 'Visit already happened outside Mindspan',
    asksServiceDate: true,
  },
  {
    key: 'member-inactive',
    label: 'Member is on hold, disenrolled, transferred or deceased',
    noteRequired: true,
  },
  {
    key: 'data-error',
    label: 'Data error (wrong appointment type, legacy type, duplicate record)',
    noteRequired: true,
  },
  { key: 'other', label: 'Other', defaultReevalDays: 30, dateOptional: true, noteRequired: true },
];

const DATA_ERROR_LABEL = DISMISS_REASONS.find((r) => r.key === 'data-error').label;

export const isDataErrorDismissal = (row) => !!row.deferredReason?.startsWith(DATA_ERROR_LABEL);

export const VISIT_RULES = {
  'ccpv-recur': {
    label: 'Care Plan Visit, 180-day renewal (99483)',
    visit: '99483',
    anchorVisit: '99483',
    anchorOffsetDays: 180,
  },
  'ccpv-first': {
    label: 'First Care Plan Visit (99483)',
    visit: '99483',
    anchorVisit: '99215',
    anchorOffsetDays: 0,
    anchorVerb: 'signed',
    heldAnchorVisit: '99205',
    heldAnchorOffsetDays: 0,
    heldAnchorVerb: 'signed',
  },
  'cfu-decision': {
    label: 'Treatment decision follow-up (99215)',
    visit: '99215',
    anchorVisit: '99483',
    anchorOffsetDays: 14,
  },
  'cfu-monitor': {
    label: 'Monitoring follow-up (99215)',
    visit: '99215',
    anchorVisit: '99215',
    anchorOffsetDays: 90,
  },
  'first-follow-up': {
    label: 'First follow-up (99215)',
    visit: '99215',
    anchorVisit: '99205',
    anchorOffsetDays: 42,
    anchorVerb: 'signed',
  },
  'testing-reassess': {
    label: 'Cognitive testing reassessment (96138)',
    visit: '96138',
    alternates: ['96136'],
    anchorVisit: '96138',
    anchorOffsetDays: 180,
  },
  'interp-after-test': {
    label: 'Testing interpretation (96132)',
    visit: '96132',
    alternates: ['99215'],
    anchorVisit: '96138',
    anchorOffsetDays: 1,
  },
  'cenp-first': { label: 'New member evaluation (99205)', visit: '99205' },
};

export const VISIT_NAMES = {
  99483: 'Care Plan Visit',
  99215: 'follow-up',
  96138: 'cognitive testing',
  96136: 'cognitive testing',
  96132: 'testing interpretation',
  99205: 'new member evaluation',
};

export const ATHENA_VISIT_TYPES = {
  99483: { typeName: 'Cognitive Care Plan Visit', reason: 'Annual Cognitive Care Plan' },
  99215: { typeName: 'Cognitive Follow-Up', reason: 'Cognitive Follow-Up' },
  96138: { typeName: 'Cognitive Testing, Technician', reason: 'Cognitive Testing, Technician' },
  96136: { typeName: 'Cognitive Testing', reason: 'Cognitive Testing' },
  96132: { typeName: 'Cognitive Testing Interpret', reason: 'Cognitive Testing Interpret' },
  99205: { typeName: 'Cognitive Evaluation, New PT', reason: 'New Patient Cognitive Evaluation' },
};

export const STATE_CHIPS = [
  { key: 'overdue', label: 'Overdue' },
  { key: 'due', label: 'Due' },
  { key: 'held', label: 'Held' },
  { key: 'deferred', label: 'Deferred' },
  { key: 'dismissed', label: 'Dismissed' },
  { key: 'booked', label: 'Booked' },
];
export const DEFAULT_STATES = ['overdue', 'due'];
export const VISIT_CHIPS = ['99483', '99215', '96138', '96132'];

export const bookableCodes = (ruleId) => {
  const rule = VISIT_RULES[ruleId];
  return rule ? [rule.visit, ...(rule.alternates ?? [])] : [];
};

const isDeferred = (row) => row.heldUntil && new Date(row.heldUntil) > new Date();

const dueStateFor = (row) => {
  if (row.bookedVisit) return 'booked';
  if (row.dismissedAt) return 'dismissed';
  if (isDeferred(row)) return 'deferred';
  if ([TASK_PRIORITY.High, TASK_PRIORITY.Critical].includes(row.priority)) return 'overdue';
  if (row.priority === TASK_PRIORITY.Low) return 'held';
  return 'due';
};

export const toVisitRow = (row) => ({
  ...row,
  ruleId: row.evidence?.sourceRef,
  daysPastDue: Math.max(0, -daysUntil(row.due)),
  dueState: dueStateFor(row),
  lapsed: row.priority === TASK_PRIORITY.Critical,
});

export const formatShortDate = (value) =>
  value
    ? new Date(value).toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '—';

export const currentDeferral = (row) => {
  const latest = [...(row.visitActivity ?? [])]
    .reverse()
    .find((event) => event.kind === 'deferred' || event.kind === 'dismissed');
  if (latest) return latest;
  if (!row.deferredReason) return null;
  return { reason: row.deferredReason, heldUntil: row.heldUntil, at: row.dismissedAt };
};

export const activityEvents = (row) => [
  ...[...(row.visitActivity ?? [])].reverse(),
  ...(row.due ? [{ kind: 'created', at: row.due, by: 'Orchestration Engine' }] : []),
];

export const formatBadgeDate = (value) =>
  value ? new Date(value).toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) : '—';

export const memberMeta = (row) =>
  [row.patient?.clinician, row.patient?.athenaIntegration?.department].filter(Boolean).join(' · ');

const anchorConfig = (row) => {
  const rule = VISIT_RULES[row.ruleId];
  if (!rule) return null;
  if (row.dueState === 'held' && rule.heldAnchorVisit) {
    return {
      visit: rule.heldAnchorVisit,
      offsetDays: rule.heldAnchorOffsetDays,
      verb: rule.heldAnchorVerb,
    };
  }
  if (row.dueState === 'held') return null;
  return { visit: rule.anchorVisit, offsetDays: rule.anchorOffsetDays, verb: rule.anchorVerb };
};

const anchorDate = (row) => {
  const config = anchorConfig(row);
  if (!config?.visit || !row.due) return null;
  return new Date(new Date(row.due).getTime() - config.offsetDays * 24 * 60 * 60 * 1000);
};

export const anchorShort = (row) => {
  const config = anchorConfig(row);
  const date = anchorDate(row);
  if (!config?.visit || !date) return '—';
  return `${config.visit} · ${formatShortDate(date)}`;
};

export const anchorLabel = (row) => {
  const config = anchorConfig(row);
  const date = anchorDate(row);
  if (!config?.visit || !date) return null;
  const name = VISIT_NAMES[config.visit] ?? config.visit;
  const verb = config.verb ?? 'completed';
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} (${config.visit}) ${verb} ${formatShortDate(date)}`;
};

export const groupVisitRows = (rows, states, visits) =>
  Object.entries(VISIT_RULES)
    .map(([ruleId, rule]) => ({
      ruleId,
      label: rule.label,
      rows: rows
        .filter(
          (row) =>
            row.ruleId === ruleId &&
            states.includes(row.dueState) &&
            (visits.length === 0 || visits.includes(rule.visit)),
        )
        .sort((a, b) => b.daysPastDue - a.daysPastDue),
    }))
    .filter((group) => group.rows.length > 0);
